import { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { Input } from '@/components/ui/input';
import { ChevronRight, ChevronDown, Folder, ClipboardList, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useTranslation } from 'react-i18next';

export default function DialogExportReports({ open, onClose }) {
    const { t } = useTranslation();
    const [folders, setFolders] = useState([]);
    const [subfolders, setSubfolders] = useState([]);
    const [selectedFolderIds, setSelectedFolderIds] = useState(new Set());
    const [selectedSubfolderIds, setSelectedSubfolderIds] = useState(new Set());
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Charger les données à l'ouverture
    useEffect(() => {
        if (!open) return;
        setIsLoading(true);
        setSelectedFolderIds(new Set());
        setSelectedSubfolderIds(new Set());
        setExpandedNodes(new Set());
        setSearchQuery('');
        horodatyApi
            .getFoldersWithReports()
            .then((res) => {
                setFolders(res?.data?.folders ?? []);
                setSubfolders(res?.data?.subfolders ?? []);
            })
            .catch(() => {
                toast.error(t('folders:impossibleDeChargerLesDossiers'));
                setFolders([]);
                setSubfolders([]);
            })
            .finally(() => setIsLoading(false));
    }, [open]);

    // Construire l'arbre hiérarchique
    const tree = useMemo(() => {
        const subfoldersByFolder = {};
        const subfoldersByParent = {};

        subfolders.forEach((sf) => {
            if (sf.parent_subfolder_uniq_id) {
                if (!subfoldersByParent[sf.parent_subfolder_uniq_id]) {
                    subfoldersByParent[sf.parent_subfolder_uniq_id] = [];
                }
                subfoldersByParent[sf.parent_subfolder_uniq_id].push(sf);
            } else {
                if (!subfoldersByFolder[sf.folder_uniq_id]) {
                    subfoldersByFolder[sf.folder_uniq_id] = [];
                }
                subfoldersByFolder[sf.folder_uniq_id].push(sf);
            }
        });

        const buildChildren = (parentUniqId, folderUniqId) => {
            const children = subfoldersByParent[parentUniqId] || [];
            return children.map((sf) => ({
                id: sf.uniq_id,
                name: sf.name,
                ref: sf.ref,
                nodeType: 'subfolder',
                subfolderType: sf.type,
                hasReport: sf.has_report,
                folderUniqId,
                children: buildChildren(sf.uniq_id, folderUniqId),
            }));
        };

        return folders.map((folder) => ({
            id: folder.uniq_id,
            name: folder.name,
            ref: folder.ref,
            nodeType: 'folder',
            hasReport: folder.has_report,
            children: (subfoldersByFolder[folder.uniq_id] || []).map((sf) => ({
                id: sf.uniq_id,
                name: sf.name,
                ref: sf.ref,
                nodeType: 'subfolder',
                subfolderType: sf.type,
                hasReport: sf.has_report,
                folderUniqId: folder.uniq_id,
                children: buildChildren(sf.uniq_id, folder.uniq_id),
            })),
        }));
    }, [folders, subfolders]);

    // Filtrer l'arbre par recherche
    const filteredTree = useMemo(() => {
        if (!searchQuery.trim()) return tree;
        const query = searchQuery.toLowerCase();

        const filterNode = (node) => {
            const matches = node.name.toLowerCase().includes(query);
            const filteredChildren = node.children?.map(filterNode).filter(Boolean) || [];
            if (matches || filteredChildren.length > 0) {
                return { ...node, children: filteredChildren };
            }
            return null;
        };

        return tree.map(filterNode).filter(Boolean);
    }, [tree, searchQuery]);

    // Collecter tous les IDs avec rapport dans un sous-arbre
    const collectReportIds = useCallback((node) => {
        const folderIds = [];
        const subfolderIds = [];
        if (node.hasReport) {
            if (node.nodeType === 'folder') folderIds.push(node.id);
            else subfolderIds.push(node.id);
        }
        for (const child of node.children || []) {
            const childIds = collectReportIds(child);
            folderIds.push(...childIds.folderIds);
            subfolderIds.push(...childIds.subfolderIds);
        }
        return { folderIds, subfolderIds };
    }, []);

    // Toggle un nœud (et ses enfants récursivement)
    const toggleNode = useCallback(
        (node) => {
            const { folderIds, subfolderIds } = collectReportIds(node);
            const isCurrentlyChecked =
                (node.hasReport &&
                    (node.nodeType === 'folder'
                        ? selectedFolderIds.has(node.id)
                        : selectedSubfolderIds.has(node.id))) ||
                (!node.hasReport &&
                    folderIds.every((id) => selectedFolderIds.has(id)) &&
                    subfolderIds.every((id) => selectedSubfolderIds.has(id)) &&
                    (folderIds.length > 0 || subfolderIds.length > 0));

            setSelectedFolderIds((prev) => {
                const next = new Set(prev);
                folderIds.forEach((id) => (isCurrentlyChecked ? next.delete(id) : next.add(id)));
                return next;
            });
            setSelectedSubfolderIds((prev) => {
                const next = new Set(prev);
                subfolderIds.forEach((id) => (isCurrentlyChecked ? next.delete(id) : next.add(id)));
                return next;
            });
        },
        [selectedFolderIds, selectedSubfolderIds, collectReportIds],
    );

    // Déterminer l'état de la checkbox d'un nœud
    const getCheckState = useCallback(
        (node) => {
            const { folderIds, subfolderIds } = collectReportIds(node);
            if (folderIds.length === 0 && subfolderIds.length === 0) return false;
            const allChecked =
                folderIds.every((id) => selectedFolderIds.has(id)) &&
                subfolderIds.every((id) => selectedSubfolderIds.has(id));
            const someChecked =
                folderIds.some((id) => selectedFolderIds.has(id)) ||
                subfolderIds.some((id) => selectedSubfolderIds.has(id));
            if (allChecked) return true;
            if (someChecked) return 'indeterminate';
            return false;
        },
        [selectedFolderIds, selectedSubfolderIds, collectReportIds],
    );

    // Tout sélectionner / désélectionner
    const totalSelected = selectedFolderIds.size + selectedSubfolderIds.size;
    const allReportIds = useMemo(() => {
        const fIds = [];
        const sfIds = [];
        for (const node of tree) {
            const ids = collectReportIds(node);
            fIds.push(...ids.folderIds);
            sfIds.push(...ids.subfolderIds);
        }
        return { folderIds: fIds, subfolderIds: sfIds };
    }, [tree, collectReportIds]);

    const allSelected =
        totalSelected > 0 && totalSelected === allReportIds.folderIds.length + allReportIds.subfolderIds.length;

    const toggleAll = () => {
        if (allSelected) {
            setSelectedFolderIds(new Set());
            setSelectedSubfolderIds(new Set());
        } else {
            setSelectedFolderIds(new Set(allReportIds.folderIds));
            setSelectedSubfolderIds(new Set(allReportIds.subfolderIds));
        }
    };

    const toggleExpand = (nodeId) => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) next.delete(nodeId);
            else next.add(nodeId);
            return next;
        });
    };

    // Export
    const handleExport = async () => {
        if (totalSelected === 0) return;
        setIsExporting(true);
        try {
            const response = await horodatyApi.exportFolderReportsZip(
                [...selectedFolderIds],
                [...selectedSubfolderIds],
            );

            const contentDisposition = response.headers['content-disposition'];
            let fileName = `horodaty_exports_dossiers_${new Date().toISOString().slice(0, 10)}.zip`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/);
                if (match?.[1]) fileName = match[1];
            }

            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t('folders:rapportsExportesAvecSucces'));
            onClose();
        } catch {
            toast.error(t('folders:impossibleDExporterLesRapports'));
        } finally {
            setIsExporting(false);
        }
    };

    // Composant récursif pour un nœud de l'arbre
    const TreeNode = ({ node, depth = 0 }) => {
        const isExpanded = expandedNodes.has(node.id);
        const hasChildren = node.children?.length > 0;
        const isProcedure = node.subfolderType === 'procedure' || node.subfolderType === 'PROCEDURE';
        const checkState = getCheckState(node);
        const { folderIds, subfolderIds } = collectReportIds(node);
        const hasAnyReport = folderIds.length > 0 || subfolderIds.length > 0;

        return (
            <div>
                <div
                    className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors whitespace-nowrap',
                        hasAnyReport ? 'cursor-pointer hover:bg-surface-active' : 'opacity-50',
                    )}
                    style={{ paddingLeft: `${12 + depth * 16}px` }}
                    onClick={() => {
                        if (hasAnyReport) toggleNode(node);
                        if (hasChildren) toggleExpand(node.id);
                    }}
                >
                    {/* Chevron */}
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(node.id);
                            }}
                            className="p-0.5 hover:bg-surface-active rounded"
                        >
                            {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                        </button>
                    ) : (
                        <span className="w-5" />
                    )}

                    {/* Checkbox */}
                    <Checkbox
                        checked={checkState}
                        disabled={!hasAnyReport}
                        onCheckedChange={(e) => {
                            e?.stopPropagation?.();
                            toggleNode(node);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />

                    {/* Icône */}
                    {node.nodeType === 'folder' ? (
                        <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : isProcedure ? (
                        <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                        <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}

                    {/* Nom */}
                    <span className="text-sm text-foreground">{node.name}</span>

                    {/* Badge rapport */}
                    {node.hasReport && (
                        <span className="text-xs text-secondary bg-secondary/10 px-1.5 py-0.5 rounded shrink-0">
                            PDF
                        </span>
                    )}
                </div>

                {/* Enfants */}
                {hasChildren && isExpanded && (
                    <div>
                        {node.children.map((child) => (
                            <TreeNode key={child.id} node={child} depth={depth + 1} />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] !flex !flex-col">
                <DialogHeader className="shrink-0">
                    <DialogTitle className="text-lg font-semibold text-foreground">{t('folders:exporterLesRapports')}</DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                ) : folders.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">{t('folders:aucunDossierAvecUnRapportDisponible')}</p>
                ) : (
                    <div className="flex flex-col gap-3 min-h-0 flex-1">
                        <div className="flex items-center justify-between shrink-0">
                            <p className="text-sm text-muted-foreground">
                                {totalSelected} rapport{totalSelected > 1 ? 's' : ''} {t('folders:selectionne')}
                                {totalSelected > 1 ? 's' : ''}
                            </p>
                            <Button variant="ghost" size="sm" onClick={toggleAll}>
                                {allSelected ? t('folders:toutDeselectionner') : t('folders:toutSelectionner')}
                            </Button>
                        </div>

                        <Input
                            type="text"
                            placeholder={t('folders:rechercherUnDossier')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="shrink-0"
                        />

                        <div className="overflow-auto border border-border rounded-lg p-2 min-h-0 flex-1">
                            {filteredTree.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground text-sm">
                                    {searchQuery ? t('folders:aucunResultat') : t('folders:aucunDossierDisponible')}
                                </div>
                            ) : (
                                <div className="w-max min-w-full space-y-1">
                                    {filteredTree.map((node) => (
                                        <TreeNode key={node.id} node={node} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 shrink-0">
                    <DialogClose asChild>
                        <Button variant="soft" color="neutral" disabled={isExporting}>
                            {t('folders:annuler')}
                        </Button>
                    </DialogClose>
                    <SubmitButton onClick={handleExport} disabled={isExporting || totalSelected === 0}>
                        {isExporting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                {t('folders:export')}
                            </>
                        ) : (
                            t('folders:exporter')
                        )}
                    </SubmitButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
